import React, { useState, useEffect, useRef, useCallback } from 'react';

const GRAPH_BUILDER_WS = (() => {
  const base = process.env.REACT_APP_GRAPH_BUILDER_API_URL || 'http://localhost:8000';
  return base.replace(/^http/, 'ws') + '/api/ednl/ws/narratives';
})();

const GRAPH_BUILDER_API = process.env.REACT_APP_GRAPH_BUILDER_API_URL || 'http://localhost:8000';

const STAGE_COLORS = {
  cdc: '#3b82f6',
  narrative: '#8b5cf6',
  aggregate: '#f59e0b',
  aggregate_hit: '#ef4444',
  frame: '#10b981',
  process: '#06b6d4',
};

const STAGE_ICONS = {
  cdc: '📡',
  narrative: '📝',
  aggregate: '📊',
  aggregate_hit: '🚨',
  frame: '📋',
  process: '⚙️',
};

const DemoEDNLPanel = ({ workspaceId }) => {
  const [events, setEvents] = useState([]);
  const [running, setRunning] = useState(false);
  const [connected, setConnected] = useState(false);
  const [summary, setSummary] = useState(null);
  const wsRef = useRef(null);
  const eventsEndRef = useRef(null);

  const connect = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState <= 1) return;
    try {
      const ws = new WebSocket(GRAPH_BUILDER_WS);
      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onerror = () => setConnected(false);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.event_type && msg.event_type.startsWith('ednl_')) {
            setEvents(prev => [...prev, {
              id: Date.now() + Math.random(),
              type: msg.event_type,
              payload: msg.payload || {},
              ts: msg.timestamp,
            }]);
          }
        } catch {}
      };
      wsRef.current = ws;
    } catch {}
  }, []);

  useEffect(() => {
    connect();
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [connect]);

  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const handleRun = async () => {
    setEvents([]);
    setSummary(null);
    setRunning(true);
    if (!connected) connect();
    try {
      const res = await fetch(`${GRAPH_BUILDER_API}/api/ednl/demo/healthcare/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId || '3120785d-027c-4eda-b20c-adf7cc31de0d',
          speed_ms: 1500,
        }),
      });
      const data = await res.json();
      setSummary(data);
    } catch (err) {
      setSummary({ ok: false, error: err.message });
    } finally {
      setRunning(false);
    }
  };

  const renderEvent = (ev) => {
    const stage = ev.payload?.stage || ev.type.replace('ednl_', '');
    const color = STAGE_COLORS[stage] || '#64748b';
    const icon = STAGE_ICONS[stage] || '•';
    const label = ev.payload?.stage_label || ev.type;

    return (
      <div key={ev.id} style={{
        borderLeft: `4px solid ${color}`,
        padding: '10px 14px',
        marginBottom: '8px',
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '0 6px 6px 0',
        animation: 'fadeIn 0.3s ease-in',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ fontSize: '16px' }}>{icon}</span>
          <span style={{ fontWeight: 600, color, fontSize: '13px' }}>{label}</span>
          {ev.payload?.session_number && (
            <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: 'auto' }}>
              Session #{ev.payload.session_number}
            </span>
          )}
        </div>
        {stage === 'narrative' && ev.payload?.text && (
          <div style={{ fontSize: '12px', color: '#cbd5e1', lineHeight: 1.5, marginTop: '4px' }}>
            {ev.payload.text}
          </div>
        )}
        {stage === 'aggregate' && (
          <div style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '4px' }}>
            <span style={{ fontFamily: 'monospace' }}>
              {ev.payload.current_count}/{ev.payload.threshold} sessions
            </span>
            {ev.payload.fired && (
              <span style={{ color: '#ef4444', fontWeight: 700, marginLeft: '8px' }}>
                ⚡ THRESHOLD FIRED
              </span>
            )}
            <div style={{
              marginTop: '6px', height: '6px', background: '#1e293b',
              borderRadius: '3px', overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', width: `${Math.min((ev.payload.current_count / ev.payload.threshold) * 100, 100)}%`,
                background: ev.payload.fired ? '#ef4444' : '#f59e0b',
                borderRadius: '3px', transition: 'width 0.5s ease',
              }} />
            </div>
          </div>
        )}
        {stage === 'aggregate_hit' && ev.payload?.narrative_text && (
          <div style={{ fontSize: '12px', color: '#fca5a5', marginTop: '4px', fontWeight: 500 }}>
            {ev.payload.narrative_text}
          </div>
        )}
        {stage === 'frame' && (
          <div style={{ fontSize: '12px', color: '#6ee7b7', marginTop: '4px' }}>
            Frame closed — {ev.payload.event_count} events accumulated
            {ev.payload.composite_narrative && (
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                {ev.payload.composite_narrative.substring(0, 200)}...
              </div>
            )}
          </div>
        )}
        {stage === 'process' && (
          <div style={{ fontSize: '12px', color: '#67e8f9', marginTop: '4px' }}>
            <strong>PA Re-evaluation triggered</strong>
            {ev.payload?.decision && (
              <span style={{
                marginLeft: '8px', background: '#065f46', color: '#6ee7b7',
                padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700,
              }}>
                {ev.payload.decision}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      background: '#0f172a', borderRadius: '8px', border: '1px solid #1e293b',
      padding: '16px', height: '100%', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#f1f5f9' }}>
            EDNL Pipeline — Live
          </div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
            Event-Driven Narrative Layer • 6 PT Sessions → Auto PA Approval
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: connected ? '#22c55e' : '#ef4444',
          }} />
          <button
            onClick={handleRun}
            disabled={running}
            style={{
              padding: '6px 16px', borderRadius: '6px', border: 'none',
              background: running ? '#334155' : '#3b82f6', color: '#fff',
              fontWeight: 600, fontSize: '13px', cursor: running ? 'not-allowed' : 'pointer',
            }}
          >
            {running ? 'Running…' : '▶ Run EDNL Demo'}
          </button>
        </div>
      </div>

      {/* Stage legend */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '11px' }}>
        {Object.entries(STAGE_COLORS).map(([k, c]) => (
          <span key={k} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#94a3b8' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: c, display: 'inline-block' }} />
            {k.replace('_', ' ')}
          </span>
        ))}
      </div>

      {/* Events stream */}
      <div style={{
        flex: 1, overflowY: 'auto', minHeight: 0,
        border: '1px solid #1e293b', borderRadius: '6px', padding: '8px',
      }}>
        {events.length === 0 && !running && (
          <div style={{ color: '#475569', textAlign: 'center', padding: '40px 0', fontSize: '13px' }}>
            Click "Run EDNL Demo" to replay 6 PT sessions for John R.<br />
            through the full EDNL pipeline in real time.
          </div>
        )}
        {events.map(renderEvent)}
        <div ref={eventsEndRef} />
      </div>

      {/* Summary */}
      {summary && (
        <div style={{
          marginTop: '10px', padding: '10px', borderRadius: '6px',
          background: summary.ok ? '#052e16' : '#450a0a',
          border: `1px solid ${summary.ok ? '#166534' : '#7f1d1d'}`,
          fontSize: '12px', color: summary.ok ? '#86efac' : '#fca5a5',
        }}>
          {summary.ok ? (
            <>✅ {summary.sessions_processed} sessions processed • {summary.aggregate_hits} threshold hit(s) • {summary.closed_frames?.length || 0} frame(s) closed</>
          ) : (
            <>❌ {summary.error}</>
          )}
        </div>
      )}
    </div>
  );
};

export default DemoEDNLPanel;
